/**
 * Dark / light mode composable.
 *
 * - Persisted to localStorage as `wecam-theme` ("light" | "dark" | "system")
 * - System preference tracked via matchMedia and applied when mode = "system"
 * - Toggles the `dark` class on <html> for Tailwind's darkMode: "class"
 */
import { ref, watch, type Ref } from "vue";

type Mode = "light" | "dark" | "system";

const STORAGE_KEY = "wecam-theme";

const mode: Ref<Mode> = ref("system");
const resolved: Ref<"light" | "dark"> = ref("light");

function applyResolved(next: "light" | "dark") {
  resolved.value = next;
  if (next === "dark") {
    document.documentElement.classList.add("dark");
  } else {
    document.documentElement.classList.remove("dark");
  }
}

function readStorage(): Mode {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored === "light" || stored === "dark" || stored === "system") return stored;
  return "system";
}

function computeResolved(m: Mode): "light" | "dark" {
  if (m === "system") {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  return m;
}

export function useTheme() {
  function init() {
    mode.value = readStorage();
    applyResolved(computeResolved(mode.value));

    // React to system preference changes when in "system" mode
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    mq.addEventListener("change", () => {
      if (mode.value === "system") {
        applyResolved(mq.matches ? "dark" : "light");
      }
    });

    // React to programmatic mode changes
    watch(mode, (m) => {
      localStorage.setItem(STORAGE_KEY, m);
      applyResolved(computeResolved(m));
    });
  }

  function setMode(m: Mode) {
    mode.value = m;
  }

  function toggle() {
    // Cycle: light -> dark -> system -> light
    setMode(
      mode.value === "light" ? "dark" : mode.value === "dark" ? "system" : "light"
    );
  }

  return { mode, resolved, init, setMode, toggle };
}
